import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { useDocumentSearchData } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import { useDocumentResultsInteractions } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsInteractions'
import { useDocumentResultsKeyboardNavigation } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsKeyboardNavigation'
import { useDocumentResultsViewState } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsViewState'
import { ContextMenu } from 'features/knowledgeDocumentViewer/components/ContextMenu'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import React from 'react'
import styled from 'styled-components'

import { AgenticSearchResultsHeader } from './AgenticSearchResultsHeader'
import { DocumentResultsContent } from './DocumentResultsContent'
import {
  UnifiedSearchHeader,
  type UnifiedSearchHeaderProps,
} from './UnifiedSearchHeader'

type DocumentResultsSectionProps = Omit<
  UnifiedSearchHeaderProps,
  'onViewModeChange'
>

const MIN_AGENTIC_PANEL_WIDTH_PERCENT = 16
const MAX_AGENTIC_PANEL_WIDTH_PERCENT = 45

function clampAgenticPanelWidth(widthPercent: number): number {
  return Math.min(
    MAX_AGENTIC_PANEL_WIDTH_PERCENT,
    Math.max(MIN_AGENTIC_PANEL_WIDTH_PERCENT, widthPercent)
  )
}

function getAgenticPanelWidthForPreviewWidth(
  leftPaneWidthPercent: number
): number {
  const previewPaneWidthPercent = 100 - leftPaneWidthPercent
  return clampAgenticPanelWidth(
    (previewPaneWidthPercent * 100) / (100 + previewPaneWidthPercent)
  )
}

const AgenticResultsLayout = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`

const AgenticSidePanelSlot = styled.div<{ $widthPercent: number }>`
  display: flex;
  flex: 0 0 ${({ $widthPercent }) => $widthPercent}%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`

const AgenticResultsContent = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`

const AgenticResizeHandle = styled.div`
  position: relative;
  z-index: 1;
  width: 0;
  flex: 0 0 0;
  cursor: col-resize;
  background: transparent;
  touch-action: none;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -0.25rem;
    width: 0.5rem;
  }

  &:hover {
    background: color-mix(in srgb, var(--border) 45%, transparent);
  }
`

export function DocumentResultsSection({
  ...headerProps
}: DocumentResultsSectionProps): React.ReactElement {
  const { activeTabId, isGeckoUser } = useKnowledgeDocumentUXContext()
  const {
    onSelectDocument,
    onOpenNewTab,
    onOpenChatbot,
    onAddRecentDocument,
    onSnapshotSearchResults,
  } = useSearchActions()
  const {
    documentResults,
    documentTotalCount,
    isLoadingDocuments,
    elasticsearchHighlightsMap,
    rawSearchData,
    searchLatencyMs,
    searchedSystemDocuments,
  } = useDocumentSearchData()
  const {
    siteId,
    searchState,
    contentFilter,
    viewMode,
    selectedDocumentId,
    pagination,
    sorting,
    setPagination,
    setSorting,
    setSelectedDocumentId,
    onSearchStateChange,
    handleViewModeChange,
    isLinkedDocsMode,
    isActive,
    leftPaneWidthPercent,
    searchMode,
    setLeftPaneWidthPercent,
    showIndividualScores,
  } = useAdvancedSearchUX()

  const documentResultsViewState = useDocumentResultsViewState({
    activeTabId,
    isGeckoUser,
    siteId,
    searchState,
    contentFilter,
    viewMode,
    selectedDocumentId,
    documentResults,
    elasticsearchHighlightsMap,
    rawSearchData,
    searchLatencyMs,
    isLinkedDocsMode,
    showIndividualScores,
    onSearchStateChange,
    onSelectDocument,
    onOpenNewTab,
    onAddRecentDocument,
    onSnapshotSearchResults,
    setSelectedDocumentId,
    handleViewModeChange,
  })
  const {
    activeDocument,
    filteredDocuments,
    handleOpenPreview,
    onSearchDocumentNavigate,
    onViewModeChange,
    setActiveIndex,
  } = documentResultsViewState

  const documentResultsInteractions = useDocumentResultsInteractions({
    onSearchDocumentNavigate,
    onOpenChatbot,
    onOpenPreview: handleOpenPreview,
    onSearchStateChange,
  })
  const {
    contextMenuPosition,
    handleContextMenuClose,
    handleContextMenuOpen,
    handleContextMenuOpenInNewTab,
    handleContextMenuAddToContext,
  } = documentResultsInteractions

  useDocumentResultsKeyboardNavigation({
    activeDocument,
    documentResults,
    enabled: isActive && contentFilter === 'documents',
    filteredDocuments,
    mode: 'documents',
    onSearchDocumentNavigate,
    selectedDocumentId,
    setActiveIndex,
    setSelectedDocumentId,
    viewMode,
  })

  const showAgenticSidePanel =
    headerProps.isAgenticSearchExperience &&
    searchMode === 'agentic' &&
    !isLinkedDocsMode
  const headerContent = showAgenticSidePanel ? undefined : (
    <UnifiedSearchHeader {...headerProps} onViewModeChange={onViewModeChange} />
  )
  const agenticResultsLayoutRef = React.useRef<HTMLDivElement | null>(null)
  const [agenticPanelWidthPercent, setAgenticPanelWidthPercent] =
    React.useState(() =>
      getAgenticPanelWidthForPreviewWidth(leftPaneWidthPercent)
    )
  const handleAgenticResizeMouseDown = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      const layout = agenticResultsLayoutRef.current
      if (!layout) return

      const layoutRect = layout.getBoundingClientRect()

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const nextPanelWidth =
          ((moveEvent.clientX - layoutRect.left) / layoutRect.width) * 100
        setAgenticPanelWidthPercent(clampAgenticPanelWidth(nextPanelWidth))
      }

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    []
  )

  const documentResultsContent = (
    <DocumentResultsContent
      data={{
        documentResults,
        documentTotalCount,
        searchedSystemDocuments,
        isLoadingDocuments,
      }}
      interactions={documentResultsInteractions}
      isLinkedDocsMode={isLinkedDocsMode}
      layout={{
        leftPaneWidthPercent,
        setLeftPaneWidthPercent,
      }}
      headerContent={headerContent}
      showCardFilterBar={!showAgenticSidePanel}
      tableState={{
        pagination,
        setPagination,
        sorting,
        setSorting,
        tableFilters: searchState.filters,
      }}
      viewState={documentResultsViewState}
      viewMode={viewMode}
    />
  )

  return (
    <>
      {showAgenticSidePanel ? (
        <AgenticResultsLayout ref={agenticResultsLayoutRef}>
          <AgenticSidePanelSlot $widthPercent={agenticPanelWidthPercent}>
            <AgenticSearchResultsHeader
              turns={headerProps.agenticTurns}
              searchInput={headerProps.searchInput}
              onSearchChange={headerProps.onSearchChange}
              onSearchKeyDown={headerProps.onAgenticSearchKeyDown}
              onSearchSubmit={headerProps.onAgenticSearchSubmit}
              contentFilter={contentFilter}
              variant='sidePanel'
            />
          </AgenticSidePanelSlot>
          <AgenticResizeHandle
            role='separator'
            aria-orientation='vertical'
            aria-valuemin={MIN_AGENTIC_PANEL_WIDTH_PERCENT}
            aria-valuemax={MAX_AGENTIC_PANEL_WIDTH_PERCENT}
            aria-valuenow={Math.round(agenticPanelWidthPercent)}
            onMouseDown={handleAgenticResizeMouseDown}
          />
          <AgenticResultsContent>
            {documentResultsContent}
          </AgenticResultsContent>
        </AgenticResultsLayout>
      ) : (
        documentResultsContent
      )}

      <ContextMenu
        position={contextMenuPosition}
        onClose={handleContextMenuClose}
        onOpen={handleContextMenuOpen}
        onOpenInNewTab={handleContextMenuOpenInNewTab}
        onAddToContext={handleContextMenuAddToContext}
        showNavigationOptions={true}
        showAddToContext={true}
      />
    </>
  )
}
